<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="modal-overlay">
        <div
          ref="modalRef"
          class="modal"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :style="{ maxWidth: props.maxWidth }"
        >
          <div class="modal__header">
            <h2 :id="titleId" class="modal__title">
              <slot name="title">{{ title }}</slot>
            </h2>
            <FeedbackTriggerButton />
            <button
              v-if="showClose"
              class="overlay-header-button modal__close"
              type="button"
              :aria-label="t('common.close')"
              @click="handleClose"
            >
              ✕
            </button>
          </div>
          <div class="modal__body">
            <slot />
          </div>
          <div v-if="$slots.footer" class="modal__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, toRef, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFocusTrap } from '@/composables/useFocusTrap'
import FeedbackTriggerButton from '@/components/molecules/FeedbackTriggerButton.vue'

const { t } = useI18n()

// The dialog is named by its heading (WCAG 4.1.2 / axe aria-dialog-name).
const titleId = useId()

interface Props {
  isOpen: boolean
  title?: string
  showClose?: boolean
  maxWidth?: string
}

interface Emits {
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  showClose: true,
  maxWidth: '600px',
})

const emit = defineEmits<Emits>()

const modalRef = ref<HTMLElement | null>(null)

function handleClose() {
  emit('close')
}

useFocusTrap(modalRef, toRef(props, 'isOpen'), { onEscape: handleClose })
</script>

<style src="@/assets/styles/overlay-header-button.css"></style>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

/*
 * Capped at the viewport, and the scroll belongs to `__body` rather than to the box
 * itself: the title and the action row then keep their place however tall the content
 * is. While the whole box scrolled, a dialog with enough content pushed its own buttons
 * past the fold, and the reader had to discover they could scroll to reach them (#4362).
 */
.modal {
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border-default);
}

.modal__title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
}




/* `min-height: 0` is what lets a flex child shrink below its content and scroll. */
.modal__body {
  padding: var(--spacing-xl);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.modal__footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border-default);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

/* Transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95) translateY(-20px);
}

@media (--breakpoint-md-down) {
  .modal {
    max-height: 95vh;
    margin: var(--spacing-md);
  }

  .modal__header,
  .modal__body,
  .modal__footer {
    padding: var(--spacing-lg);
  }
}
</style>
